import React, { useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { Badge, Button } from '@/components';
import { FormModal } from '@/design-system/form-modal';
import { Input, InputRoot } from '@/design-system/input';
import { toast } from '@/design-system/toaster';
import {
  shipmentsAPI,
  type ShipmentAdditionalNote,
  type ShipmentExim,
} from '@/services/api/shipments';

import { resolveClientNoteFields, toNoteKey } from './clientNoteFields';

/**
 * Ops-filled recon values on a DIRECT_4W shipment, beside the Dokumen Resmi
 * uploads — same idea (ops attests something the driver's app cannot capture),
 * different payload: text instead of a PDF.
 *
 * The slots come from the client's field list, NOT from the database. A note
 * with no value is never stored, so "not filled yet" is simply an absent key —
 * which is also what makes the write-once rule cheap: key absent = fillable,
 * key present = locked, with no empty state in between and no placeholder rows.
 *
 * Filling is one-way. The server refuses a second write, so the confirm step
 * below is the only thing standing between a typo and a permanently wrong value
 * in the client's CSV — it echoes the exact trimmed string that will be frozen.
 */

interface NoteRow {
  key: string;
  label: string;
  note?: ShipmentAdditionalNote;
  /** A key stored on the shipment that the client's field list doesn't name. */
  adHoc: boolean;
}

// Re-exported: the key rule now lives with the Patimban rule, shared with the
// ad-hoc document keys in DokumenResmiSection.
export { toNoteKey };

interface InformasiTambahanSectionProps {
  shipmentId: string;
  /** Client display name — picks the field list, same source as the wizard. */
  clientName?: string | null;
  exim?: string | null;
  notes?: Record<string, ShipmentAdditionalNote> | null;
  /** Called with the merged map after a successful save. */
  onUpdated: (notes: Record<string, ShipmentAdditionalNote>) => void;
}

const InformasiTambahanSection: React.FC<InformasiTambahanSectionProps> = ({
  shipmentId,
  clientName,
  exim,
  notes,
  onUpdated,
}) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<NoteRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // A free-form row exists only here until it is saved — an unfilled note is
  // never stored, so discarding one is local state, not a delete request.
  const [custom, setCustom] = useState<{ label: string; value: string } | null>(
    null
  );

  const rows = useMemo<NoteRow[]>(() => {
    const expected = resolveClientNoteFields(
      clientName ?? '',
      (exim as ShipmentExim) ?? 'GENERAL'
    );
    const fromList: NoteRow[] = expected.map((f) => ({
      key: f.key,
      label: f.label,
      note: notes?.[f.key],
      adHoc: false,
    }));
    // Anything already stored that the list doesn't name still has to render —
    // an ad-hoc key from intake, or a field the list dropped later (FO Number on
    // an import booking created before that change). Read-only either way.
    const named = new Set(expected.map((f) => f.key));
    const extras: NoteRow[] = Object.entries(notes ?? {})
      .filter(([key]) => !named.has(key))
      .map(([key, note]) => ({ key, label: note.label || key, note, adHoc: true }));
    return [...fromList, ...extras];
  }, [clientName, exim, notes]);

  const customKey = toNoteKey(custom?.label ?? '');
  const customTaken = rows.some((r) => r.key === customKey);
  // The value under confirmation comes from the free-form row when that is what
  // is being saved, otherwise from the per-key drafts. Trimmed at the source so
  // the dialog shows exactly the string that gets frozen.
  const confirmingValue = confirming
    ? (confirming.key === customKey && custom
        ? custom.value
        : (drafts[confirming.key] ?? '')
      ).trim()
    : '';

  if (rows.length === 0 && !custom) return null;

  const save = async (row: NoteRow, value: string) => {
    setBusy(row.key);
    try {
      const merged = await shipmentsAPI.saveNote(shipmentId, {
        key: row.key,
        label: row.label,
        value,
      });
      onUpdated(merged);
      setDrafts((d) => {
        const next = { ...d };
        delete next[row.key];
        return next;
      });
      setCustom(null);
      toast.success(`${row.label} tersimpan`);
    } catch (err) {
      toast.error(`Gagal menyimpan ${row.label}`, {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  return (
    <section data-testid="informasi-tambahan">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
        Informasi Tambahan
      </p>

      <div className="rounded-gsm border border-gray-200 bg-white">
        {rows.map((row, i) => {
          const draft = drafts[row.key] ?? '';
          const trimmed = draft.trim();
          return (
            <div
              key={row.key}
              data-testid={`note-row-${row.key}`}
              className={`flex items-center gap-3 px-4 py-3 ${
                i > 0 ? 'border-t border-gray-100' : ''
              }`}
            >
              <i className="ri-price-tag-3-line text-[20px] text-gray-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold tracking-tight text-gray-900">
                  {row.label}
                </p>
                {row.note ? (
                  <p className="mt-0.5 truncate text-[11px] text-gray-500">
                    {row.note.filledBy
                      ? `oleh ${row.note.filledBy} · ${dayjs(
                          row.note.filledAt
                        ).format('YYYY-MM-DD HH:mm')}`
                      : 'diisi saat pembuatan'}
                  </p>
                ) : (
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    Belum diisi · sekali disimpan tidak bisa diubah
                  </p>
                )}
              </div>

              {row.note ? (
                <div className="flex items-center gap-2">
                  <span
                    data-testid={`note-value-${row.key}`}
                    className="max-w-[200px] truncate font-mono text-[12px] text-gray-900"
                    title={row.note.value}
                  >
                    {row.note.value}
                  </span>
                  <Badge tone="success">Terkunci</Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <InputRoot size="md" className="w-[220px]">
                    <Input
                      data-testid={`note-input-${row.key}`}
                      value={draft}
                      placeholder={`Isi ${row.label}`}
                      disabled={busy !== null}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [row.key]: e.target.value }))
                      }
                    />
                  </InputRoot>
                  <Button
                    variant="secondary"
                    size="small"
                    data-testid={`note-save-${row.key}`}
                    disabled={busy !== null || trimmed.length === 0}
                    onClick={() => setConfirming(row)}
                  >
                    {busy === row.key ? (
                      <i className="mr-1.5 animate-spin text-[14px] ri-loader-4-line" />
                    ) : null}
                    Simpan
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {custom && (
          <div className="border-t border-gray-100 px-4 py-3">
            <div className="flex items-center gap-3">
              <i className="ri-price-tag-3-line text-[20px] text-gray-400" />
              <InputRoot size="md" className="w-[200px]">
                <Input
                  value={custom.label}
                  placeholder="Nama kolom"
                  disabled={busy !== null}
                  onChange={(e) =>
                    setCustom((c) => c && { ...c, label: e.target.value })
                  }
                />
              </InputRoot>
              <InputRoot size="md" className="w-[220px]">
                <Input
                  value={custom.value}
                  placeholder="Isi nilainya"
                  disabled={busy !== null}
                  onChange={(e) =>
                    setCustom((c) => c && { ...c, value: e.target.value })
                  }
                />
              </InputRoot>
              <Button
                variant="secondary"
                size="small"
                disabled={
                  busy !== null ||
                  customKey.length === 0 ||
                  customTaken ||
                  custom.value.trim().length === 0
                }
                onClick={() =>
                  setConfirming({
                    key: customKey,
                    label: custom.label.trim(),
                    adHoc: true,
                  })
                }
              >
                Simpan
              </Button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => setCustom(null)}
                className="text-[11px] text-gray-500 underline underline-offset-2 hover:text-gray-900 disabled:opacity-50"
              >
                Batal
              </button>
            </div>
            {/* The export templates read notes by FIXED key, so a key ops
                invents matches no column. Saying so here is the only warning
                they get — once saved it is frozen and still invisible. */}
            <p className="ml-8 mt-2 text-[11px] text-amber-700">
              <i className="ri-alert-line mr-1 text-[12px]" />
              Kolom bebas <strong>tidak ikut ter-export ke CSV</strong> dan
              namanya terlihat oleh klien. Untuk masuk CSV, pakai kolom yang
              sudah ada di atas.
            </p>
            {customKey.length > 0 && (
              <p className="ml-8 mt-1 font-mono text-[11px] text-gray-500">
                key: {customKey}
                {customTaken && (
                  <span className="ml-2 font-sans text-red-600">
                    sudah dipakai kolom di atas
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </div>

      {!custom && (
        <button
          type="button"
          onClick={() => setCustom({ label: '', value: '' })}
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500 underline underline-offset-2 hover:text-gray-900"
        >
          <i className="ri-add-line text-[12px] no-underline" />
          Tambah kolom lain
        </button>
      )}

      {/* FormModal, not the @/components Modal: this section lives INSIDE the
          shipment detail panel, which is itself a FormModal. The panel's
          overlay sits above a non-Radix dialog and swallows its clicks, so the
          confirm button becomes unclickable — caught by the e2e, invisible to
          jsdom. The two dialogs already opened from this panel
          (ForceTerminalModal, EditPackageModal) use FormModal for the same
          reason. */}
      <FormModal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        icon={<i className="ri-lock-line" />}
        iconTone="warning"
        title={`Simpan ${confirming?.label ?? ''}?`}
        subtitle="Nilai ini tidak bisa diubah atau dihapus setelah disimpan."
        size="sm"
        closeOnBackdropClick={busy === null}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="white"
              disabled={busy !== null}
              onClick={() => setConfirming(null)}
            >
              Batal
            </Button>
            <Button
              data-testid="note-confirm-save"
              disabled={busy !== null}
              onClick={() => confirming && save(confirming, confirmingValue)}
            >
              {busy ? 'Menyimpan…' : 'Ya, kunci nilainya'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          Pastikan nomornya benar. Yang akan disimpan persis seperti ini:
        </p>
        {/* Echo the exact TRIMMED string that gets frozen, not the raw input —
            a trailing space is precisely the typo with no way back. */}
        <p
          data-testid="note-confirm-value"
          className="mt-3 break-all rounded-gsm border border-gray-200 bg-tint px-3 py-2 font-mono text-[13px] tracking-tight text-gray-900"
        >
          {confirmingValue}
        </p>
      </FormModal>

    </section>
  );
};

export default InformasiTambahanSection;
